import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Menu, X, CheckCircle2,
  Loader2, Award, ArrowLeft, Download, ShieldCheck, ExternalLink, Play, Lock, AlertTriangle, RotateCcw,
} from 'lucide-react';
import { generateCertificatePDF } from '@/lib/certificatePdf';
import { ModuleCelebration, StreakChip } from './ModuleCelebration';
import { ElementRenderer, ElementIcon, getLearningStyle } from './elements';

// Content elements count as complete once viewed
const VIEW_COMPLETE_TYPES = ['text', 'trainer_tip', 'video', 'audio', 'downloadable'];
const completesOnView = (el) => VIEW_COMPLETE_TYPES.includes(el?.type)
  || (el?.type === 'image_hotspot' && !(el.config?.image_hotspot || el.config || {}).require_all_viewed);

// Keep completions monotonic so a slower response can never re-lock an element
const mergeProgress = (prev, next) => {
  if (!next) return prev;
  const ids = new Set([...(prev?.completed_element_ids || []), ...(next.completed_element_ids || [])]);
  return { ...next, completed_element_ids: [...ids] };
};

const CoursePlayer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();

  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentModuleIdx, setCurrentModuleIdx] = useState(0);
  const [currentElementIdx, setCurrentElementIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [completionCert, setCompletionCert] = useState(null);
  const [showCertModal, setShowCertModal] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [resumePrompt, setResumePrompt] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [streak, setStreak] = useState(0);
  const [celebration, setCelebration] = useState(null);
  const completedModulesRef = useRef(null);
  const contentRef = useRef(null);

  // Flatten all elements for navigation
  const allElements = course?.modules?.flatMap((m, mi) =>
    (m.elements || []).map((e, ei) => ({ ...e, moduleIdx: mi, elementIdx: ei, moduleTitle: m.title }))
  ) || [];

  const currentFlatIdx = allElements.findIndex(
    e => e.moduleIdx === currentModuleIdx && e.elementIdx === currentElementIdx
  );
  const currentElement = allElements[currentFlatIdx];
  const currentModule = course?.modules?.[currentModuleIdx];

  // Module progression lock: check if all elements in a module are completed
  const isModuleCompleted = useCallback((moduleIdx) => {
    const mod = course?.modules?.[moduleIdx];
    if (!mod) return false;
    const elements = mod.elements || [];
    if (elements.length === 0) return true;
    return elements.every(el => progress?.completed_element_ids?.includes(el.id));
  }, [course, progress]);

  const completedSet = new Set(progress?.completed_element_ids || []);
  const finalId = course?.final_assessment_element_id;
  const prereqIds = course?.final_assessment_prerequisite_ids || [];
  const prereqRemaining = prereqIds.filter(id => !completedSet.has(id));
  const isLocked = (el) => !!el && el.id === finalId && prereqRemaining.length > 0;
  // Drip content: module unlocks N days after enrolment
  const moduleUnlockDate = (mod) => {
    const days = mod?.unlock_after_days || 0;
    if (!days) return null;
    const base = progress?.enrolled_at ? new Date(progress.enrolled_at) : new Date();
    const d = new Date(base.getTime() + days * 86400000);
    return d > new Date() ? d : null;
  };
  const fmtDate = (d) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const requiredIds = allElements.filter(e => e.is_required !== false).map(e => e.id);
  const allRequiredDone = requiredIds.length > 0 && requiredIds.every(id => completedSet.has(id));

  useEffect(() => {
    loadCourse();
  }, [courseId]);

  const loadCourse = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [courseRes, progressRes] = await Promise.all([
        api.get(`/player/courses/${courseId}`),
        api.get(`/player/progress/${courseId}`).catch(() => ({ data: null })),
      ]);
      setCourse(courseRes.data);
      const p = progressRes.data;
      setProgress(p);
      api.get('/gamification/profile').then(r => setStreak(r.data?.current_streak || 0)).catch(() => {});

      // Resume saved position with prompt
      if (p?.current_module_id && p?.current_element_id) {
        const modules = courseRes.data.modules || [];
        const mIdx = modules.findIndex(m => m.id === p.current_module_id);
        if (mIdx >= 0) {
          const eIdx = (modules[mIdx].elements || []).findIndex(e => e.id === p.current_element_id);
          if (eIdx >= 0) {
            const elementTitle = modules[mIdx].elements[eIdx]?.title || 'your last element';
            const moduleTitle = modules[mIdx].title || '';
            setResumePrompt({ mIdx, eIdx, elementTitle, moduleTitle, pct: Math.round(p.overall_progress_pct) });
            // Auto-navigate to saved position
            setCurrentModuleIdx(mIdx);
            setCurrentElementIdx(eIdx);
          }
        }
      }
    } catch (err) {
      if (err.response?.status === 404) setCourse(null);
      else setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const savePosition = useCallback(async (modIdx, elIdx) => {
    const mod = course?.modules?.[modIdx];
    const el = mod?.elements?.[elIdx];
    if (!mod || !el) return;
    try {
      await api.post(`/player/progress/${courseId}`, {
        current_module_id: mod.id,
        current_element_id: el.id,
      });
    } catch (err) {
      console.error('Failed to save position:', err);
    }
  }, [api, courseId, course]);

  const markElementComplete = async (elementId, moduleId = currentModule?.id) => {
    if (progress?.completed_element_ids?.includes(elementId)) return;
    // Optimistic: unlock Next immediately
    setProgress(prev => ({ ...(prev || {}), completed_element_ids: [...new Set([...(prev?.completed_element_ids || []), elementId])] }));
    try {
      setSaving(true);
      const res = await api.post(`/player/progress/${courseId}`, {
        element_id: elementId,
        element_completed: true,
        current_module_id: moduleId,
        current_element_id: elementId,
      });
      setProgress(prev => mergeProgress(prev, res.data));

      // Record gamification activity
      const hour = new Date().getHours();
      api.post('/gamification/record-activity', {
        action: 'complete_element', hour,
      }).then(r => {
        if (typeof r.data?.current_streak === 'number') setStreak(r.data.current_streak);
        if (r.data?.new_badges?.length > 0) {
          r.data.new_badges.forEach(b => toast.success(`Badge earned: ${b.name}!`));
        }
      }).catch(() => {});
    } catch {
      setProgress(prev => ({ ...prev, completed_element_ids: (prev?.completed_element_ids || []).filter(id => id !== elementId) }));
      toast.error('Could not save your progress. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveElementData = async (elementId, data) => {
    try {
      const res = await api.post(`/player/progress/${courseId}`, {
        element_id: elementId,
        element_data: data,
        current_module_id: currentModule?.id,
        current_element_id: elementId,
      });
      setProgress(prev => mergeProgress(prev, res.data));
    } catch (err) {
      console.error('Failed to save element data:', err);
    }
  };

  const resetModule = async (moduleIdx) => {
    const mod = course?.modules?.[moduleIdx];
    if (!mod) return;
    try {
      const res = await api.post(`/player/progress/${courseId}/reset-module/${mod.id}`);
      setProgress(res.data);
      setCurrentModuleIdx(moduleIdx);
      setCurrentElementIdx(0);
      toast.info('Module progress has been reset. Please review the material again.');
    } catch {
      toast.error('Failed to reset module');
    }
  };

  const goToElement = (modIdx, elIdx) => {
    setCurrentModuleIdx(modIdx);
    setCurrentElementIdx(elIdx);
    setResumePrompt(null);
    savePosition(modIdx, elIdx);
    contentRef.current?.querySelector('[data-radix-scroll-area-viewport]')?.scrollTo({ top: 0, behavior: 'smooth' });
    if (window.innerWidth < 768) setSidebarOpen(false);
  };

  const goToFirstIncomplete = () => {
    const target = allElements.find(e => prereqIds.includes(e.id) && !completedSet.has(e.id));
    if (target) goToElement(target.moduleIdx, target.elementIdx);
  };

  // Auto-complete content pages once viewed
  const currentId = currentElement?.id;
  useEffect(() => {
    if (!currentElement || !progress || !completesOnView(currentElement) || isLocked(currentElement) || moduleUnlockDate(currentModule)) return undefined;
    if (progress.completed_element_ids?.includes(currentElement.id)) return undefined;
    const t = setTimeout(() => markElementComplete(currentElement.id, currentModule?.id), 1000);
    return () => clearTimeout(t);
  }, [currentId, !!progress]); // eslint-disable-line react-hooks/exhaustive-deps

  // Celebrate modules that become complete during this session
  const modules = course?.modules || [];
  const completedModuleIds = modules.filter(m => (m.elements || []).length > 0 && m.elements.every(e => completedSet.has(e.id))).map(m => m.id);
  const completedKey = completedModuleIds.join(',');
  useEffect(() => {
    if (!progress || !course) return;
    if (completedModulesRef.current === null) { completedModulesRef.current = new Set(completedModuleIds); return; }
    const fresh = completedModuleIds.filter(id => !completedModulesRef.current.has(id));
    fresh.forEach(id => completedModulesRef.current.add(id));
    if (!fresh.length) return;
    const modIdx = modules.findIndex(m => m.id === fresh[fresh.length - 1]);
    const req = allElements.filter(e => e.is_required !== false);
    setCelebration({
      moduleTitle: modules[modIdx]?.title,
      progressPct: req.length ? Math.round((req.filter(e => completedSet.has(e.id)).length / req.length) * 100) : 100,
      streak,
      hasNext: modIdx < modules.length - 1,
      nextModuleIdx: modIdx + 1,
    });
  }, [completedKey, !!progress, !!course]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setCelebration(c => (c ? { ...c, streak } : c)); }, [streak]);

  const continueAfterCelebration = () => {
    const next = celebration;
    setCelebration(null);
    if (next?.hasNext && currentModuleIdx < next.nextModuleIdx) goToElement(next.nextModuleIdx, 0);
  };

  const goNext = () => {
    if (currentFlatIdx < allElements.length - 1) {
      const next = allElements[currentFlatIdx + 1];
      goToElement(next.moduleIdx, next.elementIdx);
    }
  };

  const goPrev = () => {
    if (currentFlatIdx > 0) {
      const prev = allElements[currentFlatIdx - 1];
      goToElement(prev.moduleIdx, prev.elementIdx);
    }
  };

  const handleComplete = async () => {
    try {
      const res = await api.post(`/player/courses/${courseId}/complete`);
      if (res.data.completed) {
        toast.success('Course completed! Certificate generated.');
        setProgress(prev => ({ ...prev, status: 'completed', overall_progress_pct: 100 }));
        if (res.data.certificate) {
          setCompletionCert(res.data.certificate);
          setShowCertModal(true);
        }
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot complete course yet');
    }
  };

  const handleDownloadCert = async () => {
    if (!completionCert) return;
    setDownloadingPdf(true);
    try {
      await generateCertificatePDF(completionCert);
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const isElementCompleted = (elementId) => {
    return progress?.completed_element_ids?.includes(elementId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#20acf6]" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
        <div className="text-center space-y-3 max-w-sm" data-testid="player-load-error">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
          <h2 className="text-lg font-bold text-[#2a3e5d]">Oops, something went wrong loading this course</h2>
          <p className="text-sm text-slate-600">Your progress is saved. Please check your connection and try again.</p>
          <Button onClick={loadCourse} className="bg-[#20acf6] hover:bg-[#3bb8f7] min-h-[44px]" data-testid="player-retry-btn">
            <RotateCcw className="h-4 w-4 mr-2" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-[#2a3e5d]">Course not found</h2>
          <Button onClick={() => navigate('/courses')} className="mt-4">Browse Courses</Button>
        </div>
      </div>
    );
  }

  const progressPct = requiredIds.length ? Math.round((requiredIds.filter(id => completedSet.has(id)).length / requiredIds.length) * 100) : 0;
  const currentDone = currentElement && completedSet.has(currentElement.id);
  const currentDripDate = moduleUnlockDate(currentModule);
  const canProceed = !currentElement || currentDone || isLocked(currentElement) || !!currentDripDate;

  return (
    <div className="h-screen flex flex-col bg-white" data-testid="course-player">
      <ModuleCelebration data={celebration} onContinue={continueAfterCelebration} onClose={() => setCelebration(null)} />
      {/* Top bar */}
      <header className="h-14 bg-[#2a3e5d] text-white flex items-center px-4 shrink-0 z-20">
        <Button
          variant="ghost" size="sm"
          className="text-white hover:bg-white/10 mr-2"
          onClick={() => navigate(-1)}
          data-testid="player-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <button
          className="md:hidden text-white mr-3"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle menu"
          data-testid="player-menu-toggle"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        <h1 className="text-sm font-semibold truncate flex-1 text-white">{course.title}</h1>
        <div className="flex items-center gap-3">
          <StreakChip streak={streak} />
          <div className="hidden sm:flex items-center gap-2 text-xs">
            <span>{Math.round(progressPct)}%</span>
            <div className="w-24 h-2 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#20acf6] rounded-full transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
          {allRequiredDone && progress?.status !== 'completed' && (
            <Button
              size="sm"
              className="bg-green-500 hover:bg-green-600 text-xs"
              onClick={handleComplete}
              data-testid="complete-course-btn"
            >
              <Award className="h-3 w-3 mr-1" /> Complete
            </Button>
          )}
          {progress?.status === 'completed' && (
            <Badge className="bg-green-500 text-white text-xs">Completed</Badge>
          )}
        </div>
      </header>

      {/* Resume prompt */}
      {resumePrompt && (
        <div className="bg-[#20acf6]/5 border-b border-[#20acf6]/20 px-4 py-2.5 flex items-center justify-between shrink-0" data-testid="resume-prompt">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[#20acf6]/10 flex items-center justify-center shrink-0">
              <Play className="h-4 w-4 text-[#20acf6]" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#2a3e5d] truncate">Continue where you left off</p>
              <p className="text-xs text-slate-500 truncate">{resumePrompt.moduleTitle} &middot; {resumePrompt.elementTitle} &middot; {resumePrompt.pct}% complete</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="ghost" className="text-xs text-slate-500 h-7" onClick={() => { setCurrentModuleIdx(0); setCurrentElementIdx(0); setResumePrompt(null); }} data-testid="resume-start-over">
              Start from beginning
            </Button>
            <Button size="sm" className="bg-[#20acf6] hover:bg-[#3bb8f7] text-xs h-7" onClick={() => setResumePrompt(null)} data-testid="resume-continue">
              Continue
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } fixed md:relative md:translate-x-0 z-10 w-72 bg-[#F8FAFC] border-r border-slate-200 h-[calc(100vh-3.5rem)] transition-transform duration-200`}
          data-testid="player-sidebar"
        >
          <ScrollArea className="h-full">
            <div className="p-4">
              <div className="mb-4">
                <Progress value={progressPct} className="h-2" />
                <p className="text-xs text-slate-500 mt-1">{Math.round(progressPct)}% complete</p>
              </div>
              {course.modules?.map((mod, mi) => {
                const modDone = isModuleCompleted(mi);
                return (
                <div key={mod.id} className="mb-3">
                  <div className="mb-2 px-2 flex items-start gap-2" data-testid={`sidebar-module-${mod.id}`}>
                    {modDone
                      ? <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" data-testid={`module-complete-icon-${mod.id}`} />
                      : <span className="h-4 w-4 rounded-full border-2 border-slate-300 shrink-0 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-[#2a3e5d]">{mod.title}</h3>
                      {moduleUnlockDate(mod) && (
                        <p className="text-[10px] text-amber-700 flex items-center gap-1 mt-0.5" data-testid={`module-drip-${mod.id}`}>
                          <Lock className="h-2.5 w-2.5" /> Unlocks {fmtDate(moduleUnlockDate(mod))}
                        </p>
                      )}
                    </div>
                    <Badge
                      className={`text-[10px] px-1.5 py-0 shrink-0 ${modDone ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-slate-200 text-slate-600 hover:bg-slate-200'}`}
                      data-testid={`module-status-badge-${mod.id}`}
                    >
                      {modDone ? 'Complete' : `Incomplete · ${(mod.elements || []).filter(e => completedSet.has(e.id)).length}/${(mod.elements || []).length}`}
                    </Badge>
                  </div>
                  <div className="space-y-0.5">
                    {(mod.elements || []).map((el, ei) => {
                      const isActive = mi === currentModuleIdx && ei === currentElementIdx;
                      const done = isElementCompleted(el.id);
                      const locked = isLocked(el);
                      return (
                        <button
                          key={el.id}
                          onClick={() => goToElement(mi, ei)}
                          className={`w-full text-left px-3 py-2.5 min-h-[44px] rounded-lg text-sm flex items-center gap-2 transition-colors ${
                            isActive
                              ? 'bg-[#20acf6] text-white'
                              : done
                              ? 'bg-green-50 text-green-800 hover:bg-green-100'
                              : 'hover:bg-slate-100 text-slate-700'
                          }`}
                          data-testid={`sidebar-element-${el.id}`}
                        >
                          {locked ? (
                            <Lock className={`h-4 w-4 shrink-0 ${isActive ? 'text-white' : 'text-slate-400'}`} data-testid={`sidebar-lock-${el.id}`} />
                          ) : done ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
                          ) : (
                            <ElementIcon type={el.type} className="h-4 w-4 shrink-0" style={{ color: isActive ? 'white' : getLearningStyle(el.type).color }} />
                          )}
                          <span className="truncate">{el.title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>
          </ScrollArea>
        </aside>

        {/* Main content */}
        <main className="flex-1 flex flex-col overflow-hidden" data-testid="player-content" ref={contentRef}>
          <ScrollArea className="flex-1">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 element-enter" key={currentElement?.id || 'none'}>
              {currentElement && isLocked(currentElement) && (
                <div className="p-8 rounded-2xl border border-slate-200 bg-slate-50 text-center space-y-4" data-testid="final-assessment-locked">
                  <Lock className="h-10 w-10 text-slate-400 mx-auto" />
                  <h2 className="text-xl font-bold text-[#2a3e5d]">Final assessment locked</h2>
                  <p className="text-sm text-slate-600 max-w-md mx-auto">
                    Complete all earlier activities to unlock the final assessment and your certificate.
                    You have <strong data-testid="final-remaining-count">{prereqRemaining.length}</strong> left to finish.
                  </p>
                  <Button onClick={goToFirstIncomplete} className="bg-[#20acf6] hover:bg-[#3bb8f7] min-h-[44px]" data-testid="go-first-incomplete-btn">
                    Go to next incomplete activity
                  </Button>
                </div>
              )}
              {currentElement && currentDripDate && (
                <div className="p-8 rounded-2xl border border-amber-200 bg-amber-50 text-center space-y-3" data-testid="module-drip-locked">
                  <Lock className="h-10 w-10 text-amber-500 mx-auto" />
                  <h2 className="text-xl font-bold text-[#2a3e5d]">This module unlocks on {fmtDate(currentDripDate)}</h2>
                  <p className="text-sm text-slate-600 max-w-md mx-auto">Content is released in stages to help you learn at a steady pace. Come back then to continue.</p>
                </div>
              )}
              {currentElement && !currentDripDate && !isLocked(currentElement) && (
                <ElementRenderer
                  element={currentElement}
                  progress={progress}
                  onComplete={() => markElementComplete(currentElement.id)}
                  onSaveData={(data) => saveElementData(currentElement.id, data)}
                  onSubmitQuiz={async (answers) => {
                    const res = await api.post(`/player/quiz/${courseId}/${currentElement.id}/submit`, {
                      answers,
                      attempt_number: (progress?.quiz_submissions?.filter(s => s.element_id === currentElement.id).length || 0) + 1,
                    });
                    const pRes = await api.get(`/player/progress/${courseId}`);
                    setProgress(prev => mergeProgress(prev, pRes.data));
                    return res.data;
                  }}
                  isCompleted={isElementCompleted(currentElement?.id)}
                  courseId={courseId}
                  api={api}
                  currentModuleIdx={currentModuleIdx}
                  onModuleRestart={resetModule}
                />
              )}
            </div>
          </ScrollArea>

          {/* Bottom nav */}
          <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between bg-white shrink-0">
            <Button
              variant="outline"
              className="min-h-[44px]"
              disabled={currentFlatIdx <= 0}
              onClick={goPrev}
              data-testid="prev-element-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <span className="text-xs text-slate-500 hidden sm:inline">
              {currentFlatIdx + 1} of {allElements.length}
            </span>
            {currentFlatIdx < allElements.length - 1 ? (
              <div className="flex items-center gap-3">
                {!canProceed && (
                  <span className="text-[11px] sm:text-xs text-slate-500 max-w-[110px] sm:max-w-none leading-tight text-right" data-testid="next-locked-hint">Complete this activity to continue</span>
                )}
                <Button
                  onClick={goNext}
                  disabled={!canProceed}
                  className="bg-[#20acf6] hover:bg-[#3bb8f7] min-h-[44px] transition-[background-color,opacity]"
                  data-testid="next-element-btn"
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            ) : allRequiredDone && progress?.status !== 'completed' ? (
              <Button
                onClick={handleComplete}
                className="bg-green-600 hover:bg-green-700"
                data-testid="finish-course-btn"
              >
                <Award className="h-4 w-4 mr-1" /> Finish Course
              </Button>
            ) : progress?.status === 'completed' ? (
              <Badge className="bg-green-500 text-white text-xs px-3 py-1.5">Course Completed</Badge>
            ) : (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Award className="h-4 w-4" />
                <span>Complete all elements to finish</span>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Certificate Completion Modal */}
      <Dialog open={showCertModal} onOpenChange={setShowCertModal}>
        <DialogContent className="sm:max-w-md" data-testid="cert-completion-modal">
          <DialogHeader>
            <DialogTitle className="text-center text-[#2a3e5d] text-xl flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#20acf6]/20 to-[#02d1d4]/30 flex items-center justify-center">
                <Award className="h-8 w-8 text-[#2a3e5d]" />
              </div>
              Congratulations!
            </DialogTitle>
            <DialogDescription className="text-center">
              You have successfully completed this course and earned a certificate.
            </DialogDescription>
          </DialogHeader>
          {completionCert && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Course</span>
                  <span className="font-medium text-slate-800 text-right max-w-[200px] truncate">{completionCert.course_title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Certificate No.</span>
                  <span className="font-mono text-xs text-slate-700">{completionCert.certificate_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Issued</span>
                  <span className="text-slate-700">
                    {new Date(completionCert.completion_date || completionCert.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'long', year: 'numeric'
                    })}
                  </span>
                </div>
                {completionCert.is_accredited && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Accredited</span>
                    <Badge className="bg-[#02d1d4] text-[#2a3e5d] text-xs">Yes</Badge>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Button
                  className="bg-[#2a3e5d] hover:bg-[#2a4f7a] w-full"
                  onClick={handleDownloadCert}
                  disabled={downloadingPdf}
                  data-testid="download-cert-modal-btn"
                >
                  {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                  Download Certificate PDF
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  asChild
                  data-testid="verify-cert-modal-link"
                >
                  <Link to={`/verify/${completionCert.certificate_number}`} target="_blank">
                    <ShieldCheck className="h-4 w-4 mr-2" /> View Verification Page
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-slate-500"
                  asChild
                  data-testid="view-all-certs-link"
                >
                  <Link to="/certificates">
                    <ExternalLink className="h-4 w-4 mr-2" /> View All My Certificates
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default CoursePlayer;

